import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Row, Col, Button } from 'reactstrap';
import { Translate } from '../../../contexts/Language';
import { Modal } from '../../../components';
import { Service } from '../../../services';

const PAYMENT_REASONS = [
  { value: 'Investments', label: 'Investments - Transactions related to investments' },
  { value: 'InternalTransfer', label: 'Internal Transfer - Transferring funds internally' },
  { value: 'InvoiceForServicesOrGoods', label: 'Invoice - Payment for services or goods' },
  { value: 'OperatingExpense', label: 'Operating Expense - Rent, utilities, loans' },
  { value: 'StaffCosts', label: 'Staff Costs - Salaries and benefits' },
  { value: 'SettlingClientFunds', label: 'Settling Client Funds' },
  { value: 'SettlingTradingCounterpartyFunds', label: 'Settling Trading Counterparty Funds' },
  { value: 'Other', label: 'Other' },
];

const SUPPORTED_CURRENCIES = [
  { code: 'BTC', name: 'Bitcoin' },
  { code: 'ETH', name: 'Ethereum' },
  { code: 'USDT', name: 'USDT' },
  { code: 'USDC', name: 'USD Coin' },
  { code: 'BNB', name: 'BNB' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
];

export const CreatePaymentModal = ({
  show,
  formData,
  beneficiaries,
  selectedAccount,
  onChange,
  onCreate,
  onClose,
  accountData,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  // Fetch payment methods when beneficiary is selected
  useEffect(() => {
    if (formData.beneficiary_id && show) {
      setLoadingMethods(true);
      Service.UniPayment.GetPaymentMethod(formData.beneficiary_id)
        .then(response => {
          if (response && response.error === false) {
            setPaymentMethods(response.data || []);
          } else {
            setPaymentMethods([]);
          }
        })
        .catch(error => {
          console.error('Error fetching payment methods:', error);
          setPaymentMethods([]);
        })
        .finally(() => {
          setLoadingMethods(false);
        });
    } else {
      setPaymentMethods([]);
    }
  }, [formData.beneficiary_id, show]);

  return (
    <Modal
      Show={show}
      Title={Translate('Create Payment')}
      onClose={onClose}
      xlSize
    >
      <form onSubmit={e => { e.preventDefault(); onCreate(); }}>
        {selectedAccount && accountData && (
          <div className="alert alert-info mb-3">
            <small>
              {Translate('From Account')}
:
              {' '}
              <strong>
                {accountData.friendly_name || accountData.account_number}
                {' '}
-
                {' '}
                {accountData.asset_type}
                {' '}
-
                {' '}
                {accountData.available || accountData.balance}
              </strong>
            </small>
          </div>
        )}

        <FormGroup>
          <Label>
            {Translate('Beneficiary')}
            {' '}
            <span className="text-danger">*</span>
          </Label>
          <Input
            type="select"
            value={formData.beneficiary_id || ''}
            onChange={e => {
              onChange('beneficiary_id', e.target.value);
              onChange('payment_method_id', ''); // Reset payment method when beneficiary changes
            }}
            required
          >
            <option value="">{Translate('Select Beneficiary')}</option>
            {beneficiaries.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}
                {' '}
(
                {b.email}
)
              </option>
            ))}
          </Input>
        </FormGroup>

        {formData.beneficiary_id && (
          <FormGroup>
            <Label>{Translate('Payment Method')}</Label>
            <Input
              type="select"
              value={formData.payment_method_id || ''}
              onChange={e => onChange('payment_method_id', e.target.value)}
              disabled={loadingMethods}
            >
              <option value="">{loadingMethods ? Translate('Loading...') : Translate('Select Payment Method (Optional)')}</option>
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>
                  {pm.title}
                  {' '}
-
                  {pm.transfer_method}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              {Translate('Either select a payment method or provide a destination account ID')}
            </small>
          </FormGroup>
        )}

        <FormGroup>
          <Label>{Translate('To Account ID')}</Label>
          <Input
            type="text"
            value={formData.to_account_id || ''}
            onChange={e => onChange('to_account_id', e.target.value)}
            placeholder={Translate('Destination account ID (if not using payment method)')}
          />
        </FormGroup>

        <Row>
          <Col md={8}>
            <FormGroup>
              <Label>
                {Translate('Amount')}
                {' '}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                // min="0"
                value={formData.amount || ''}
                onChange={e => onChange('amount', e.target.value)}
                placeholder={Translate('Enter amount')}
                required
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>
                {Translate('Asset Type')}
                {' '}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={formData.asset_type || ''}
                onChange={e => onChange('asset_type', e.target.value)}
                required
              >
                <option value="">{Translate('Select Currency')}</option>
                {SUPPORTED_CURRENCIES.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.name}
                    {' '}
(
                    {currency.code}
)
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label>
            {Translate('Reason')}
            {' '}
            <span className="text-danger">*</span>
          </Label>
          <Input
            type="select"
            value={formData.reason || ''}
            onChange={e => onChange('reason', e.target.value)}
            required
          >
            <option value="">{Translate('Select Reason')}</option>
            {PAYMENT_REASONS.map(reason => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>{Translate('Reference')}</Label>
          <Input
            type="text"
            value={formData.reference || ''}
            onChange={e => onChange('reference', e.target.value)}
            placeholder={Translate('Payment reference (for bank transfers)')}
          />
        </FormGroup>

        <FormGroup>
          <Label>{Translate('Note')}</Label>
          <Input
            type="textarea"
            value={formData.note || ''}
            onChange={e => onChange('note', e.target.value)}
            placeholder={Translate('Additional note')}
            rows={3}
          />
        </FormGroup>

        <div className="text-right mt-3">
          <Button color="secondary" onClick={onClose} className="mr-2">
            {Translate('Cancel')}
          </Button>
          <Button color="primary" type="submit">
            {Translate('Create Payment')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
